import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CurrentHandover,
  Handover,
  HandoverList,
  HandoverReaders,
  MyHandoverLine,
  MyHandoverLineList,
  SaveHandoverRequest,
} from '@up2d8/contracts';
import { getMeta, setMeta } from '../../../offline/db';
import { HANDLES_OFFLINE } from '../../../offline/network-mode';
import { useOffline } from '../../../offline/use-offline';
import { useSession } from '../../../shared/session';

/**
 * De gegevens van de ploegoverdracht in de app (Stap 39, OVD-2).
 *
 * **Offline lezen** (spec §5): de laatst opgehaalde overdracht staat in de meta-store van de
 * offline-database, net als de tijdlijn (Stap 12), en verdwijnt daar bij uitloggen of intrekken
 * (ADR-0046). **Offline schrijven**: het concept staat lokaal en gaat mee zodra er verbinding is;
 * indienen kan pas dan.
 */

const CURRENT_KEY = (lineId: string) => `handover:current:${lineId}`;
const HANDOVER_KEY = (handoverId: string) => `handover:id:${handoverId}`;
const DRAFT_KEY = (lineId: string, slotId: string) => `handover:draft:${lineId}:${slotId}`;
const LINES_KEY = 'handover:lines';

export function useHandoverLines() {
  const { api, has } = useSession();
  const { online } = useOffline();
  return useQuery({
    queryKey: ['handover', 'my-lines'],
    enabled: has('shift_handover.handover.read'),
    ...HANDLES_OFFLINE,
    queryFn: async (): Promise<MyHandoverLine[]> => {
      if (!online) return (await getMeta<MyHandoverLine[]>(LINES_KEY)) ?? [];
      const { data, error } = await api.GET('/api/v1/handovers/my-lines', {});
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      const items = (data as MyHandoverLineList).items;
      await setMeta(LINES_KEY, items);
      return items;
    },
  });
}

/** De laatste ingediende overdracht van een lijn; offline uit de cache, met `fromCache`. */
export function useCurrentHandover(lineId: string | null) {
  const { api } = useSession();
  const { online } = useOffline();
  return useQuery({
    queryKey: ['handover', 'current', lineId, online],
    enabled: lineId !== null,
    // Offline leest de functie zelf de kopie. Gepauzeerd liep ze nooit, en bleef het scherm op het
    // skelet staan: `online` in de sleutel maakt offline een nieuwe query zonder gegevens (#4).
    ...HANDLES_OFFLINE,
    queryFn: async (): Promise<{ data: CurrentHandover | null; fromCache: boolean }> => {
      const key = CURRENT_KEY(lineId as string);
      if (!online) return { data: await getMeta<CurrentHandover>(key), fromCache: true };
      const { data, error } = await api.GET('/api/v1/handovers/current', {
        params: { query: { lineId: lineId as string } },
      });
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      await setMeta(key, data);
      return { data: data as CurrentHandover, fromCache: false };
    },
  });
}

export function useHandover(handoverId: string | null) {
  const { api } = useSession();
  const { online } = useOffline();
  return useQuery({
    queryKey: ['handover', 'one', handoverId, online],
    enabled: handoverId !== null,
    ...HANDLES_OFFLINE,
    queryFn: async (): Promise<{ data: Handover | null; fromCache: boolean }> => {
      const key = HANDOVER_KEY(handoverId as string);
      if (!online) return { data: await getMeta<Handover>(key), fromCache: true };
      const { data, error } = await api.GET('/api/v1/handovers/{handoverId}', {
        params: { path: { handoverId: handoverId as string } },
      });
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      await setMeta(key, data);
      return { data: data as Handover, fromCache: false };
    },
  });
}

export function useHandoverReaders(handoverId: string | null, enabled: boolean) {
  const { api } = useSession();
  return useQuery({
    queryKey: ['handover', 'readers', handoverId],
    enabled: enabled && handoverId !== null,
    queryFn: async (): Promise<HandoverReaders> => {
      const { data, error } = await api.GET('/api/v1/handovers/{handoverId}/readers', {
        params: { path: { handoverId: handoverId as string } },
      });
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return data as HandoverReaders;
    },
  });
}

export function useHandoverHistory(lineId: string | null, enabled = true) {
  const { api } = useSession();
  return useQuery({
    queryKey: ['handover', 'history', lineId],
    enabled: enabled && lineId !== null,
    queryFn: async (): Promise<HandoverList['items']> => {
      const { data, error } = await api.GET('/api/v1/handovers', {
        params: { query: { lineId: lineId as string } },
      });
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as HandoverList).items;
    },
  });
}

/** "Gelezen" en "Punt afsluiten": beide geven de bijgewerkte overdracht terug. */
export function useHandoverActions() {
  const { api } = useSession();
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['handover'] });

  const read = useMutation({
    mutationFn: async (handoverId: string) => {
      const { data, error } = await api.POST('/api/v1/handovers/{handoverId}/read', {
        params: { path: { handoverId } },
      });
      if (error || !data) throw error ?? new Error('Mislukt');
      return data as Handover;
    },
    onSuccess: refresh,
  });

  const closeItem = useMutation({
    mutationFn: async (input: { itemId: string; note: string }) => {
      const { data, error } = await api.POST('/api/v1/handover-items/{itemId}/close', {
        params: { path: { itemId: input.itemId } },
        body: { note: input.note },
      });
      if (error || !data) throw error ?? new Error('Mislukt');
      return data as Handover;
    },
    onSuccess: refresh,
  });

  return { read, closeItem };
}

/** Het concept voor deze lijn en dienst: van de server, of het lokale als we offline zijn. */
export function useHandoverDraft(lineId: string, slotId: string) {
  const { api } = useSession();
  const { online } = useOffline();
  return useQuery({
    queryKey: ['handover', 'draft', lineId, slotId, online],
    // Offline het lokale concept; zie `useCurrentHandover`.
    ...HANDLES_OFFLINE,
    // Een concept wordt niet stil vervangen terwijl iemand typt.
    refetchOnWindowFocus: false,
    queryFn: async (): Promise<{ data: Handover | null; fromCache: boolean }> => {
      const key = DRAFT_KEY(lineId, slotId);
      if (!online) return { data: await getMeta<Handover>(key), fromCache: true };
      const { data, error } = await api.GET('/api/v1/handovers/draft', {
        params: { query: { lineId, slotId } },
      });
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return { data: data as Handover, fromCache: false };
    },
  });
}

export async function storeDraftLocally(lineId: string, slotId: string, draft: Handover) {
  await setMeta(DRAFT_KEY(lineId, slotId), draft);
}

export function useSaveDraft(lineId: string, slotId: string) {
  const { api } = useSession();
  return useMutation({
    mutationFn: async (body: SaveHandoverRequest): Promise<Handover> => {
      const { data, error } = await api.PUT('/api/v1/handovers/draft', {
        params: { query: { lineId, slotId } },
        body,
      });
      if (error || !data) throw error ?? new Error('Opslaan mislukt');
      return data as Handover;
    },
  });
}

export function useSubmitHandover() {
  const { api } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (handoverId: string): Promise<Handover> => {
      const { data, error } = await api.POST('/api/v1/handovers/{handoverId}/submit', {
        params: { path: { handoverId } },
      });
      if (error || !data) throw error ?? new Error('Indienen mislukt');
      return data as Handover;
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['handover'] }),
  });
}

/** "do 17/09". */
export function shortDate(date: string, language: string): string {
  const moment = new Date(`${date}T12:00:00`);
  const dag = moment.toLocaleDateString(language, { weekday: 'short' }).replace('.', '');
  const dd = String(moment.getDate()).padStart(2, '0');
  const mm = String(moment.getMonth() + 1).padStart(2, '0');
  return `${dag} ${dd}/${mm}`;
}

/** "21:48". */
export function clock(iso: string, language: string): string {
  return new Date(iso).toLocaleTimeString(language, { hour: '2-digit', minute: '2-digit' });
}

/** "ma" voor "Doorgeschoven sinds ma". */
export function weekdayShort(iso: string, language: string): string {
  return new Date(iso).toLocaleDateString(language, { weekday: 'short' }).replace('.', '');
}
